"use client";

import type { ReactNode } from "react";
import { BsTwitterX } from "react-icons/bs";
import { LuChartColumn as LuBarChart2 } from "react-icons/lu";

import type { TopBountiesDiscovered } from "@/backend/focus";
import { getTopBountiesDiscovered } from "@/backend/focus";
import { Spinner } from "@/components/shared/spinner";
import { useScrollableTable } from "@/hooks/use-scrollable-table";
import { formatDecimalValue, formatUSDCents } from "@/utils/currency";
import { pluralize } from "@/utils/helpers";

import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableScrollable,
} from "../ui/table";
import TopTableProfileAvatar from "./top-table-profile-avatar";

const TABLE_HEADERS = [
  { label: "Profile" },
  { label: "Bounty" },
  { label: "Referral Bonus" },
  { label: "Page Views" },
];

type TopBountiesTableProps = {
  limit?: number;
};

const Fallback = ({ children }: { children: ReactNode }) => (
  <div className="w-full overflow-hidden rounded-2xl border">
    <Table className="custom-scrollbar">
      <TableHeader>
        <TableRow>
          {TABLE_HEADERS.map(({ label }) => (
            <TableHead
              key={label}
              className="
                cursor-pointer py-2 text-left text-xs text-muted
                first:pl-4
                md:text-sm
              "
            >
              {label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>{children}</TableRow>
      </TableBody>
    </Table>
  </div>
);

const TopBountiesTable = ({ limit = 25 }: TopBountiesTableProps) => {
  const fetchTopBounties = async (offset = 0) => {
    const response = await getTopBountiesDiscovered({
      params: {
        limit,
        offset,
      },
    });

    return {
      items: response?.Users || [],
      count: response?.Count ?? 0,
    };
  };

  const {
    ref,
    initLoading,
    loadingMoreItems,
    totalItems,
    items: topBounties,
  } = useScrollableTable<TopBountiesDiscovered>({
    fetchItems: fetchTopBounties,
    errorMessage: "An error happened when fetching top bounties",
  });

  if (initLoading) {
    return TopBountiesTable.fallback;
  }

  if (!totalItems) {
    return (
      <Fallback>
        <TableCell colSpan={4}>
          <p className="text-center">No top bounties found.</p>
        </TableCell>
      </Fallback>
    );
  }

  return (
    <>
      <div className="w-full overflow-hidden rounded-2xl border">
        <TableScrollable containerClassname="h-fit max-h-[510px] overflow-y-auto relative scrollable-table">
          <TableHeader>
            <TableRow>
              {TABLE_HEADERS.map(({ label }) => (
                <TableHead
                  key={label}
                  className="
                    unforce-radius sticky top-0 z-50 cursor-pointer
                    bg-background py-2 text-left text-xs whitespace-nowrap
                    text-muted
                    first:pl-4
                    md:text-sm
                  "
                >
                  {label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {topBounties.map((bounty, i) => {
              if (!bounty) {
                return null;
              }
              return (
                <TableRow key={i}>
                  <TableCell className="min-w-fit p-0">
                    <div className="group flex min-w-max items-center gap-2 p-2">
                      <BsTwitterX
                        className="
                          hidden shrink-0
                          sm:inline-flex
                        "
                      />
                      <TopTableProfileAvatar topBounty={bounty} />
                    </div>
                  </TableCell>

                  <TableCell
                    className="
                      font-shadow-green w-[22%] text-left font-mono text-xs
                      text-emerald-400
                      md:text-sm
                    "
                  >
                    {formatUSDCents(bounty.JoiningRewardUsdCents)}
                  </TableCell>

                  <TableCell
                    className="
                      font-shadow-green w-[22%] text-left font-mono text-xs
                      text-emerald-400
                      md:text-sm
                    "
                  >
                    {formatUSDCents(bounty.ReferralRewardUsdCents)}
                  </TableCell>

                  <TableCell
                    className="
                      relative top-1 flex items-center gap-2 text-left text-xs
                      text-muted
                      md:text-sm
                    "
                  >
                    <LuBarChart2 className="size-4 text-muted" />
                    {formatDecimalValue(bounty.PageViews)}
                  </TableCell>
                </TableRow>
              );
            })}

            {((!initLoading && topBounties.length !== totalItems) ||
              loadingMoreItems) && (
              <TableRow>
                <TableCell colSpan={4} ref={ref}>
                  <div className="flex justify-center gap-x-2">
                    <Spinner />
                    <span>Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TableScrollable>
      </div>

      <div className="mt-4 text-center text-xs text-muted">
        Total: {pluralize(totalItems, "bounty")} discovered
      </div>
    </>
  );
};

TopBountiesTable.fallback = (
  <Fallback>
    {TABLE_HEADERS.map(({ label }) => (
      <TableCell key={label}>
        <Skeleton className="h-6 w-full" />
      </TableCell>
    ))}
  </Fallback>
);

export default TopBountiesTable;
